import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle } from "@headlessui/react";
import tw from "twin.macro";
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";
import { usePayments } from "hooks/usePayments.js";
import { useOwners } from "hooks/useOwners.js";
import moment from "moment";
import { useCatalogs } from "hooks/useCatalogs";
import FilterByDates from "components/misc/FilterByDates";
import PaymentsTable from "components/tables/PaymentsTable";
import {useParams} from 'react-router-dom';

const Container = tw.div`relative`;
const Table = tw.table`min-w-full bg-white rounded-lg shadow-md`;
const Th = tw.th`py-3 px-4 bg-primary-500 text-white font-semibold text-left`;
const Td = tw.td`py-2 px-4 border-b border-gray-200 text-gray-700`;
const Tr = tw.tr`hover:bg-gray-100 transition-colors`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight`;
const StatusBadge = styled.span(({ status }) => [
  tw`px-3 py-1 rounded-full text-xs font-bold`,
  status === 0 && tw`bg-yellow-400 text-yellow-900`, // Pending
  status === 1 && tw`bg-green-500 text-white`, // Approved
  status === 2 && tw`bg-red-500 text-white`, // Rejected
]);
const Button = tw.button`ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition`;

const initialPaymentForm = {
  house: '',
  ownerName: '',
  category: '',
  createdDate: '',
  balance: '',
  referenceNumber: '',
  status: 0
};

const AdminPaymentsPage = () => {
  const { getCategoryOptionsById } = useCatalogs();
  const [catalogCategories, setCatalogCategories] = useState([]);
  const { allPayments, fetchAllPayments } = usePayments(); // Assume this fetches all payments for admin
  const [paymentTable, setPaymentTable] = useState([]);
  const { owners, fetchOwners } = useOwners();
  const [loading, setLoading] = useState(true);
  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(initialPaymentForm);
  // Autocomplete state
  const [houseQuery, setHouseQuery] = useState("");
  const [ownerQuery, setOwnerQuery] = useState("");
  const [showHouseDropdown, setShowHouseDropdown] = useState(false);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const houseInputRef = useRef();
  const ownerInputRef = useRef();
  // Date filter state
  const [selectedDateStart, setSelectedDateStart] = useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = useState(null);
  const {houseOwnerId} = useParams();
  // Add payment locally (mock, not persisted)
  const handleAddPayment = (e) => {
    e.preventDefault();
    // Generate a new paymentId
    const newId = paymentTable.length > 0 ? Math.max(...paymentTable.map(p => p.paymentId)) + 1 : 1;
    const newPayment = {
      ...form,
      paymentId: newId,
      balance: Number(form.balance),
      status: Number(form.status),
    };
    // Add to paymentTable immutably so React updates state
    setPaymentTable(prev => [newPayment, ...prev]);
    setModalOpen(false);
    setForm(initialPaymentForm);
  };

  const openModal = () => {
    // If houseOwnerId is present, find the owner and prefill casa/propietario
    if (houseOwnerId) {
      const owner = owners.find(o => String(o.ownerId) === String(houseOwnerId));
      if (owner) {
        setForm({
          ...initialPaymentForm,
          house: owner.houseNumber || "",
          ownerName: owner.name || ""
        });
      } else {
        setForm(initialPaymentForm);
      }
    } else {
      setForm(initialPaymentForm);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(initialPaymentForm);
    setHouseQuery("");
    setOwnerQuery("");
    setShowHouseDropdown(false);
    setShowOwnerDropdown(false);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (e.target.name === "house") {
      setHouseQuery(e.target.value);
      setShowHouseDropdown(true);
    }
    if (e.target.name === "ownerName") {
      setOwnerQuery(e.target.value);
      setShowOwnerDropdown(true);
    }
  };

  // Filtered owners for dropdowns
  const filteredHouses = owners.filter(o => o.houseNumber.toLowerCase().includes(houseQuery.toLowerCase()));
  const filteredOwners = owners.filter(o => o.name.toLowerCase().includes(ownerQuery.toLowerCase()));

  // When a house is selected, set both house and owner fields
  const selectHouse = (owner) => {
    setForm(f => ({ ...f, house: owner.houseNumber, ownerName: owner.name }));
    setHouseQuery(owner.houseNumber);
    setOwnerQuery(owner.name);
    setShowHouseDropdown(false);
  };
  // When an owner is selected, set both owner and house fields
  const selectOwner = (owner) => {
    setForm(f => ({ ...f, ownerName: owner.name, house: owner.houseNumber }));
    setOwnerQuery(owner.name);
    setHouseQuery(owner.houseNumber);
    setShowOwnerDropdown(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchAllPayments();
      setPaymentTable(allPayments);

      await fetchOwners();
      const catalog = getCategoryOptionsById(1);
      setCatalogCategories(catalog.map(c => c.name));
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Use correct data source depending on param
    let data = !Array.isArray(paymentTable) ? [] : paymentTable;

    if (statusFilter !== 'all') {
      data = data.filter(p => String(p.status) === statusFilter);
    }
    if (search) {
      const s = search.toLowerCase();
      data = data.filter(p =>
        (p.house?.toLowerCase().includes(s)) ||
        (p.ownerName?.toLowerCase().includes(s)) ||
        (p.category?.toLowerCase().includes(s)) ||
        (p.referenceNumber?.toLowerCase().includes(s)) ||
        (p.balance && String(p.balance).toLowerCase().includes(s)) ||
        (p.createdDate && String(p.createdDate).toLowerCase().includes(s))
      );
    }
    // Date range filter
    if (selectedDateStart || selectedDateEnd) {
      data = data.filter(p => {
        if (!p.createdDate) return false;
        const date = moment(p.createdDate, [moment.ISO_8601, 'YYYY-MM-DD']);
        let afterStart = true, beforeEnd = true;
        if (selectedDateStart) afterStart = date.isSameOrAfter(selectedDateStart, 'day');
        if (selectedDateEnd) beforeEnd = date.isSameOrBefore(selectedDateEnd, 'day');
        return afterStart && beforeEnd;
      });
    }
    // Sort by paymentId desc
    data = [...data].sort((a, b) => b.paymentId - a.paymentId);
    setFiltered(data);
    setCurrentPage(1); // Reset to first page on filter change
  }, [paymentTable, houseOwnerId, statusFilter, search, selectedDateStart, selectedDateEnd ]);

  const handleApprove = (id) => {
    setPaymentTable(prev => prev.map(payment =>
      payment.paymentId === id ? { ...payment, status: 1 } : payment
    ));
  };
  const handleReject = (id) => {
    setPaymentTable(prev => prev.map(payment =>
      payment.paymentId === id ? { ...payment, status: 2 } : payment
    ));
  };

  return (
    <AnimationRevealPage>
      <Header />
      <Container className="py-12">
        <Heading>Gestión de Pagos de Residentes</Heading>
        <div className="flex flex-wrap gap-4 mt-4 justify-center">
          <input
            type="text"
            placeholder="Buscar pago..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm"
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded shadow-sm">
            <option value="all">Todos los estatus</option>
            <option value="0">Pendiente</option>
            <option value="1">Aprobado</option>
            <option value="2">Rechazado</option>
          </select>
          <Button onClick={openModal}>Nuevo Pago</Button>
        </div>
        {/* Date filter */}
        <div className="flex flex-wrap gap-4 mb-2 justify-center">
          <FilterByDates
            selectedDateStart={selectedDateStart}
            setSelectedDateStart={setSelectedDateStart}
            selectedDateEnd={selectedDateEnd}
            setSelectedDateEnd={setSelectedDateEnd}
          />
        </div>
        {/* Modal for adding payment */}
        {modalOpen && (
          <Dialog open={modalOpen} onClose={closeModal} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">Nuevo Pago</DialogTitle>
              <form onSubmit={handleAddPayment}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4 relative">
                    <label htmlFor="house" className="block mb-1 font-semibold">Casa</label>
                    <input
                      id="house"
                      name="house"
                      value={form.house}
                      onChange={handleFormChange}
                      onFocus={() => setShowHouseDropdown(true)}
                      autoComplete="off"
                      ref={houseInputRef}
                      required
                      className="w-full px-3 py-2 border rounded"
                    />
                    {showHouseDropdown && houseQuery && filteredHouses.length > 0 && (
                      <ul className="absolute z-50 bg-white border rounded w-full mt-1 max-h-40 overflow-y-auto shadow">
                        {filteredHouses.map(o => (
                          <button
                            key={o.ownerId}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-primary-100 cursor-pointer focus:outline-none"
                            onClick={() => selectHouse(o)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { selectHouse(o); } }}
                          >
                            {o.houseNumber} - {o.name}
                          </button>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="mb-4 relative">
                    <label htmlFor="ownerName" className="block mb-1 font-semibold">Propietario</label>
                    <input
                      id="ownerName"
                      name="ownerName"
                      value={form.ownerName}
                      onChange={handleFormChange}
                      onFocus={() => setShowOwnerDropdown(true)}
                      autoComplete="off"
                      ref={ownerInputRef}
                      required
                      className="w-full px-3 py-2 border rounded"
                    />
                    {showOwnerDropdown && ownerQuery && filteredOwners.length > 0 && (
                      <ul className="absolute z-50 bg-white border rounded w-full mt-1 max-h-40 overflow-y-auto shadow">
                        {filteredOwners.map(o => (
                          <button
                            key={o.ownerId}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-primary-100 cursor-pointer focus:outline-none"
                            onClick={() => selectOwner(o)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { selectOwner(o); } }}
                          >
                            {o.name} - {o.houseNumber}
                          </button>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="mb-4">
                    <label htmlFor="category" className="block mb-1 font-semibold">Categoría</label>
                    <select
                      id="category"
                      name="category"
                      value={form.category}
                      onChange={handleFormChange}
                      required
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="">Selecciona una categoria</option>
                      {catalogCategories.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="createdDate" className="block mb-1 font-semibold">Fecha</label>
                    <input id="createdDate" name="createdDate" type="date" value={form.createdDate} onChange={handleFormChange} required className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="balance" className="block mb-1 font-semibold">Monto</label>
                    <input id="balance" name="balance" type="number" value={form.balance} onChange={handleFormChange} required className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="referenceNumber" className="block mb-1 font-semibold">Referencia</label>
                    <input id="referenceNumber" name="referenceNumber" value={form.referenceNumber} onChange={handleFormChange} className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div className="mb-6">
                    <label htmlFor="status" className="block mb-1 font-semibold">Estatus</label>
                    <select id="status" name="status" value={form.status} onChange={handleFormChange} className="w-full px-3 py-2 border rounded">
                      <option value={0}>Pendiente</option>
                      <option value={1}>Aprobado</option>
                      <option value={2}>Rechazado</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button type="button" onClick={closeModal} className="bg-gray-400 hover:bg-gray-600">Cancelar</Button>
                  <Button type="submit">Agregar</Button>
                </div>
              </form>
            </div>
          </Dialog>
        )}
        <div className="overflow-x-auto bg-white/90 backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl">
          <PaymentsTable
            loading={loading}
            filtered={filtered}
            currentPage={currentPage}
            pageSize={pageSize}
            setCurrentPage={setCurrentPage}
            handleApprove={handleApprove}
            handleReject={handleReject}
            Tr={Tr}
            Td={Td}
            Th={Th}
            StatusBadge={StatusBadge}
            Button={Button}
          />
        </div>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AdminPaymentsPage;
